import axios from 'axios';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { api } from '../../../Api';
import { Store } from '../../../Store';
import { getError } from '../../../utils';
import { TONE, fmtNum, formatValue, st, statusTone } from './reportUi';

/**
 * /admin/reports/:key — one owner report.
 *
 * Everything that shapes the report (period, search, filters, breakdown,
 * sort, page) lives in the URL, so a view can be bookmarked or shared and
 * the browser Back button undoes a change. The column choice is remembered
 * per report in this browser.
 */

const PAGE_SIZES = [25, 50, 100, 200];
const colsKey = (key) => `ownerReport.columns.${key}`;
const readCols = (key) => {
  try {
    const v = JSON.parse(localStorage.getItem(colsKey(key)) || 'null');
    return Array.isArray(v) ? v : null;
  } catch {
    return null;
  }
};
const writeCols = (key, v) => {
  try { localStorage.setItem(colsKey(key), JSON.stringify(v)); } catch { /* private mode */ }
};

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function OwnerReportScreen() {
  const { key } = useParams();
  const { state } = useContext(Store);
  const { userInfo } = state;
  const headers = useMemo(() => ({ Authorization: `Bearer ${userInfo.token}` }), [userInfo.token]);
  const [params, setParams] = useSearchParams();

  const [catalog, setCatalog] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState('');
  const [showCols, setShowCols] = useState(false);
  const [columns, setColumns] = useState(null);
  const colsRef = useRef(null);

  const report = catalog?.reports.find((r) => r.key === key);
  const period = params.get('period') || 'last_30';

  // Search and filter inputs are drafts until Apply, so typing does not fire
  // a query per keystroke.
  const [draft, setDraft] = useState({});
  useEffect(() => {
    if (!report) return;
    const d = { q: params.get('q') || '', from: params.get('from') || '', to: params.get('to') || '' };
    report.filters.forEach((f) => { d[f.key] = params.get(f.key) || ''; });
    setDraft(d);
  }, [report, params]);

  useEffect(() => {
    axios.get(`${api}/api/owner-reports`, { headers })
      .then(({ data: c }) => setCatalog(c))
      .catch((err) => setError(getError(err)));
  }, [headers]);

  useEffect(() => {
    if (!report) return;
    const saved = readCols(report.key);
    const valid = saved && saved.filter((c) => report.columns.some((x) => x.key === c));
    setColumns(valid && valid.length ? valid : report.columns.filter((c) => c.default).map((c) => c.key));
  }, [report]);

  useEffect(() => {
    const close = (e) => { if (colsRef.current && !colsRef.current.contains(e.target)) setShowCols(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const queryString = useCallback((extra = {}) => {
    const q = new URLSearchParams(params);
    if (!q.get('period')) q.set('period', period);
    Object.entries(extra).forEach(([k, v]) => (v === '' || v == null ? q.delete(k) : q.set(k, v)));
    return q.toString();
  }, [params, period]);

  const load = useCallback(async () => {
    if (!report) return;
    setLoading(true);
    setError('');
    try {
      const { data: res } = await axios.get(`${api}/api/owner-reports/${report.key}?${queryString()}`, { headers });
      setData(res);
    } catch (err) {
      setError(getError(err));
    } finally {
      setLoading(false);
    }
  }, [report, queryString, headers]);

  useEffect(() => { load(); }, [load]);

  const update = (changes, keepPage = false) => {
    const q = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => (v === '' || v == null ? q.delete(k) : q.set(k, String(v))));
    if (!keepPage) q.delete('page');
    setParams(q);
  };

  const applyDraft = (e) => {
    e?.preventDefault();
    const changes = { q: draft.q?.trim() };
    report.filters.forEach((f) => { changes[f.key] = draft[f.key]; });
    if (period === 'custom') {
      if (!draft.from || !draft.to) return toast.error('Choose both a start date and an end date.');
      if (draft.from > draft.to) return toast.error('The start date must be on or before the end date.');
      changes.from = draft.from;
      changes.to = draft.to;
    }
    update(changes);
  };

  const resetAll = () => {
    const q = new URLSearchParams();
    q.set('period', period);
    setParams(q);
  };

  const pickPeriod = (p) => {
    if (p === 'custom') {
      const to = params.get('to') || todayIso();
      const from = params.get('from') || new Date(Date.now() - 29 * 86400000).toISOString().slice(0, 10);
      update({ period: 'custom', from, to });
    } else {
      update({ period: p, from: '', to: '' });
    }
  };

  const toggleCol = (c) => {
    const next = columns.includes(c) ? columns.filter((x) => x !== c) : report.columns.filter((x) => columns.includes(x.key) || x.key === c).map((x) => x.key);
    if (!next.length) return toast.error('Keep at least one column.');
    setColumns(next);
    writeCols(report.key, next);
  };

  const exportFile = async (ext) => {
    setExporting(ext);
    try {
      const qs = queryString({ columns: columns.join(','), page: '', pageSize: '', download: ext === 'pdf' ? '1' : '' });
      const res = await axios.get(`${api}/api/owner-reports/${report.key}/export.${ext}?${qs}`, { headers, responseType: 'blob' });
      const name = /filename="([^"]+)"/.exec(res.headers['content-disposition'] || '')?.[1] || `${report.key}-report.${ext}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`${ext.toUpperCase()} downloaded.`);
    } catch (err) {
      let msg = getError(err);
      if (err.response?.data instanceof Blob) {
        try { msg = JSON.parse(await err.response.data.text()).message || msg; } catch { /* keep */ }
      }
      toast.error(msg || 'The export failed.');
    } finally {
      setExporting('');
    }
  };

  if (catalog && !report) {
    return (
      <div style={st.page}>
        <div style={st.notice}>This report does not exist. <Link to="/admin/reports">Back to Reports</Link></div>
      </div>
    );
  }

  const visibleCols = report && columns ? report.columns.filter((c) => columns.includes(c.key)) : [];
  const activeFilters = report ? report.filters.filter((f) => params.get(f.key)).length + (params.get('q') ? 1 : 0) : 0;
  const page = Number(params.get('page')) || 1;

  return (
    <div style={st.page}>
      <Helmet><title>{report ? report.title : 'Report'}</title></Helmet>
      <div style={st.crumbs}><Link to="/admin/reports">Reports</Link> / {report?.short || '…'}</div>

      <div style={st.head}>
        <div>
          <h1 style={st.h1}>{report?.title || 'Loading…'}</h1>
          <p style={st.lede}>{report?.description}</p>
          {data?.range && <div style={{ ...st.muted, marginTop: 6 }}><i className="fa fa-calendar" aria-hidden="true" /> {data.range.label}</div>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={st.btnLight} onClick={load} disabled={loading}>
            <i className="fa fa-refresh" aria-hidden="true" /> Refresh
          </button>
          <button type="button" style={st.btnLight} onClick={() => exportFile('csv')} disabled={!report || !!exporting}>
            <i className="fa fa-file-excel-o" aria-hidden="true" /> {exporting === 'csv' ? 'Preparing…' : 'CSV'}
          </button>
          <button type="button" style={st.btnDark} onClick={() => exportFile('pdf')} disabled={!report || !!exporting}>
            <i className="fa fa-file-pdf-o" aria-hidden="true" /> {exporting === 'pdf' ? 'Preparing PDF…' : 'Download PDF'}
          </button>
        </div>
      </div>

      {/* ---------------------------------------------------------- controls */}
      {report && (
        <form style={st.card} onSubmit={applyDraft}>
          <div style={st.cardHead}>
            <span style={st.cardTitle}>Period</span>
          </div>
          <div style={st.chips}>
            {catalog.periods.map((p) => (
              <button key={p.key} type="button" style={period === p.key ? st.chipOn : st.chip} onClick={() => pickPeriod(p.key)}>
                {p.label}
              </button>
            ))}
          </div>
          {period === 'custom' && (
            <div style={{ ...st.grid, marginTop: 12, maxWidth: 520 }}>
              <label style={st.field}>From
                <input type="date" style={st.input} value={draft.from || ''} max={draft.to || todayIso()} onChange={(e) => setDraft({ ...draft, from: e.target.value })} />
              </label>
              <label style={st.field}>To
                <input type="date" style={st.input} value={draft.to || ''} min={draft.from || undefined} max={todayIso()} onChange={(e) => setDraft({ ...draft, to: e.target.value })} />
              </label>
            </div>
          )}

          <div style={{ ...st.cardHead, marginTop: 16 }}>
            <span style={st.cardTitle}>Search &amp; filters {activeFilters ? <span style={{ ...st.pill, background: '#dbeafe', color: '#1d4ed8', marginLeft: 6 }}>{activeFilters} active</span> : null}</span>
          </div>
          <div style={st.grid}>
            <label style={{ ...st.field, gridColumn: 'span 2', minWidth: 0 }}>Search
              <input style={st.input} placeholder={report.searchPlaceholder} value={draft.q || ''} onChange={(e) => setDraft({ ...draft, q: e.target.value })} />
            </label>
            {report.filters.map((f) => (
              <label key={f.key} style={st.field}>{f.label}
                {f.options ? (
                  <select style={st.input} value={draft[f.key] || ''} onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}>
                    {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <input style={st.input} placeholder={f.placeholder || ''} value={draft[f.key] || ''} onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })} />
                )}
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <button type="submit" style={st.btn}><i className="fa fa-search" aria-hidden="true" /> Apply</button>
            <button type="button" style={st.btnLight} onClick={resetAll}>Clear all</button>
          </div>
        </form>
      )}

      {error && <div style={{ ...st.notice, background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }}>{error}</div>}

      {/* -------------------------------------------------------------- KPIs */}
      <div style={{ ...st.stats, opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
        {(data?.summary || Array.from({ length: 5 }, () => null)).map((k, i) => (
          <div key={k?.label || i} style={st.stat}>
            <span style={st.statLabel}>{k?.label || ' '}</span>
            <span style={{ ...st.statValue, color: TONE[k?.tone] || '#111827' }}>
              {k ? (k.type === 'text' ? k.value : formatValue(k.type, k.value)) : '…'}
            </span>
            {k?.hint && <span style={st.statHint}>{k.hint}</span>}
          </div>
        ))}
      </div>

      {/* ------------------------------------------------------------- trend */}
      {data?.series && <TrendCard series={data.series} loading={loading} />}

      {/* --------------------------------------------------------- breakdown */}
      {report && data?.breakdown && (
        <div style={st.card}>
          <div style={st.cardHead}>
            <span style={st.cardTitle}>{data.breakdown.label}</span>
            <div style={st.chips}>
              {report.groups.map((g) => (
                <button key={g.value} type="button" style={(params.get('groupBy') || report.groups[0].value) === g.value ? st.chipOn : st.chip} onClick={() => update({ groupBy: g.value }, true)}>
                  {g.label}
                </button>
              ))}
            </div>
          </div>
          <BreakdownTable breakdown={data.breakdown} />
        </div>
      )}

      {/* ------------------------------------------------------------ detail */}
      {report && (
        <div style={st.card}>
          <div style={st.cardHead}>
            <span style={st.cardTitle}>
              Detailed records {data ? <span style={{ ...st.muted, textTransform: 'none', letterSpacing: 0, fontWeight: 600 }}>· {fmtNum(data.total, 0)} found</span> : null}
            </span>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <label style={{ ...st.field, flexDirection: 'row', alignItems: 'center', gap: 6 }}>Sort
                <select style={{ ...st.input, width: 'auto', padding: '6px 8px' }} value={params.get('sortBy') || 'date'} onChange={(e) => update({ sortBy: e.target.value })}>
                  {report.sorts.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <select style={{ ...st.input, width: 'auto', padding: '6px 8px' }} value={params.get('sortDir') || 'desc'} onChange={(e) => update({ sortDir: e.target.value })}>
                  <option value="desc">Newest / highest first</option>
                  <option value="asc">Oldest / lowest first</option>
                </select>
              </label>
              <label style={{ ...st.field, flexDirection: 'row', alignItems: 'center', gap: 6 }}>Rows
                <select style={{ ...st.input, width: 'auto', padding: '6px 8px' }} value={params.get('pageSize') || '25'} onChange={(e) => update({ pageSize: e.target.value })}>
                  {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              <div style={{ position: 'relative' }} ref={colsRef}>
                <button type="button" style={st.btnLight} onClick={() => setShowCols((v) => !v)}>
                  <i className="fa fa-columns" aria-hidden="true" /> Columns ({visibleCols.length}/{report.columns.length})
                </button>
                {showCols && (
                  <div style={st.popover}>
                    <div style={{ ...st.muted, marginBottom: 8 }}>Shown on screen and in the PDF / CSV.</div>
                    {report.columns.map((c) => (
                      <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13.5, cursor: 'pointer' }}>
                        <input type="checkbox" checked={columns.includes(c.key)} onChange={() => toggleCol(c.key)} />
                        {c.label}
                      </label>
                    ))}
                    <button type="button" style={{ ...st.btnLight, width: '100%', marginTop: 8 }} onClick={() => { const d = report.columns.filter((c) => c.default).map((c) => c.key); setColumns(d); writeCols(report.key, d); }}>
                      Restore default columns
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {data?.capped && (
            <div style={st.notice}>
              More than 10,000 records match. The table pages through the first 10,000; narrow the period or add a filter to see the rest.
            </div>
          )}

          <div style={{ ...st.tableWrap, opacity: loading ? 0.55 : 1 }}>
            <table style={{ ...st.table, minWidth: Math.max(visibleCols.length * 130, 700) }}>
              <thead>
                <tr>
                  <th style={{ ...st.th, width: 44 }}>#</th>
                  {visibleCols.map((c) => (
                    <th key={c.key} style={{ ...st.th, textAlign: ['money', 'points', 'number'].includes(c.type) ? 'right' : 'left' }}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data?.rows || []).map((r, i) => (
                  <tr key={r.id || `${r.kind}-${i}`}>
                    <td style={{ ...st.td, color: '#9ca3af' }}>{(page - 1) * (data.pageSize || 25) + i + 1}</td>
                    {visibleCols.map((c) => (
                      <td key={c.key} style={{ ...st.td, textAlign: ['money', 'points', 'number'].includes(c.type) ? 'right' : 'left', whiteSpace: c.type === 'datetime' ? 'nowrap' : undefined, maxWidth: ['items', 'details', 'reference'].includes(c.key) ? 320 : undefined }}>
                        {c.type === 'status' && r[c.key] ? (
                          <span style={{ ...st.pill, background: statusTone(r[c.key]).bg, color: statusTone(r[c.key]).color }}>{r[c.key]}</span>
                        ) : (
                          formatValue(c.type, r[c.key])
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                {data && !data.rows.length && !loading && (
                  <tr><td colSpan={visibleCols.length + 1} style={{ ...st.td, textAlign: 'center', color: '#6b7280', padding: 28 }}>No records match this period and these filters.</td></tr>
                )}
                {!data && loading && (
                  <tr><td colSpan={visibleCols.length + 1} style={{ ...st.td, textAlign: 'center', color: '#6b7280', padding: 28 }}>Loading…</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {data && data.pages > 1 && (
            <div style={st.pager}>
              <span style={st.muted}>Page {data.page} of {fmtNum(data.pages, 0)}</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" style={st.btnLight} disabled={data.page <= 1} onClick={() => update({ page: 1 }, true)}>« First</button>
                <button type="button" style={st.btnLight} disabled={data.page <= 1} onClick={() => update({ page: data.page - 1 }, true)}>‹ Previous</button>
                <button type="button" style={st.btnLight} disabled={data.page >= data.pages} onClick={() => update({ page: data.page + 1 }, true)}>Next ›</button>
                <button type="button" style={st.btnLight} disabled={data.page >= data.pages} onClick={() => update({ page: data.pages }, true)}>Last »</button>
              </div>
            </div>
          )}
          <div style={{ ...st.muted, marginTop: 10 }}>
            PDF and CSV include up to 5,000 records with the columns selected above, in the current sort order.
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ trend */

function TrendCard({ series, loading }) {
  const [active, setActive] = useState(series.keys[0]?.key);
  useEffect(() => {
    if (!series.keys.some((k) => k.key === active)) setActive(series.keys[0]?.key);
  }, [series, active]);
  const rows = series.rows || [];
  const vals = rows.map((r) => Number(r[active]) || 0);
  const max = Math.max(...vals, 0);
  const total = vals.reduce((a, b) => a + b, 0);
  const H = 150;
  const barW = Math.max(Math.min(900 / Math.max(rows.length, 1), 34), 6);
  const W = Math.max(rows.length * barW, 300);
  const [hover, setHover] = useState(null);
  const label = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  const every = Math.ceil(rows.length / 12);

  return (
    <div style={{ ...st.card, opacity: loading ? 0.55 : 1 }}>
      <div style={st.cardHead}>
        <span style={st.cardTitle}>Daily trend</span>
        <div style={st.chips}>
          {series.keys.map((k) => (
            <button key={k.key} type="button" style={active === k.key ? st.chipOn : st.chip} onClick={() => setActive(k.key)}>{k.label}</button>
          ))}
        </div>
      </div>
      {rows.length === 0 || max === 0 ? (
        <div style={{ ...st.muted, padding: '20px 0', textAlign: 'center' }}>Nothing recorded in this period.</div>
      ) : (
        <>
          <div style={{ ...st.muted, marginBottom: 6 }}>
            {hover !== null
              ? <>{label(rows[hover].date)}: <b style={{ color: '#111827' }}>{fmtNum(vals[hover])}</b></>
              : <>Total <b style={{ color: '#111827' }}>{fmtNum(total)}</b> · peak <b style={{ color: '#111827' }}>{fmtNum(max)}</b> · daily average <b style={{ color: '#111827' }}>{fmtNum(total / rows.length)}</b></>}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <svg width={W} height={H + 22} role="img" aria-label="Daily trend chart" onMouseLeave={() => setHover(null)}>
              {[0.25, 0.5, 0.75, 1].map((f) => (
                <line key={f} x1={0} x2={W} y1={H - H * f} y2={H - H * f} stroke="#f1f5f9" />
              ))}
              {rows.map((r, i) => {
                const h = max ? Math.max((vals[i] / max) * (H - 8), vals[i] ? 2 : 0) : 0;
                return (
                  <g key={r.date} onMouseEnter={() => setHover(i)}>
                    <rect x={i * barW} y={0} width={barW} height={H} fill="transparent" />
                    <rect x={i * barW + barW * 0.15} y={H - h} width={barW * 0.7} height={h} rx={2} fill={hover === i ? '#1d4ed8' : '#60a5fa'} />
                    {i % every === 0 && (
                      <text x={i * barW + barW / 2} y={H + 15} fontSize={10} fill="#6b7280" textAnchor="middle">{label(r.date)}</text>
                    )}
                  </g>
                );
              })}
            </svg>
          </div>
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------- breakdown */

function BreakdownTable({ breakdown }) {
  const [showAll, setShowAll] = useState(false);
  const rows = showAll ? breakdown.rows : breakdown.rows.slice(0, 15);
  // Bar in the first numeric column, so the biggest line stands out at a glance.
  const numericIdx = breakdown.columns.findIndex((_, i) => i > 0 && breakdown.rows.some((r) => typeof r[i] === 'number'));
  const barIdx = breakdown.columns.findIndex((c, i) => i > 0 && /(tk|point|value|credited|amount)/i.test(c) && breakdown.rows.some((r) => typeof r[i] === 'number'));
  const idx = barIdx > 0 ? barIdx : numericIdx;
  const max = idx > 0 ? Math.max(...breakdown.rows.map((r) => Number(r[idx]) || 0), 0) : 0;

  if (!breakdown.rows.length) return <div style={{ ...st.muted, padding: '12px 0' }}>No data for this breakdown in the period.</div>;
  return (
    <>
      <div style={st.tableWrap}>
        <table style={st.table}>
          <thead>
            <tr>
              {breakdown.columns.map((c, i) => <th key={c} style={{ ...st.th, textAlign: i === 0 ? 'left' : 'right' }}>{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri}>
                {r.map((c, i) => (
                  <td key={i} style={{ ...st.td, textAlign: i === 0 ? 'left' : 'right', fontWeight: i === 0 ? 600 : 400 }}>
                    {typeof c === 'number' ? fmtNum(c) : c}
                    {i === idx && max > 0 && (
                      <div style={{ background: '#f1f5f9', height: 4, borderRadius: 4, marginTop: 4 }}>
                        <div style={{ background: '#2563eb', height: 4, borderRadius: 4, width: `${Math.round(((Number(c) || 0) / max) * 100)}%`, marginLeft: 'auto' }} />
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {breakdown.rows.length > 15 && (
        <button type="button" style={{ ...st.btnLight, marginTop: 10 }} onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Show fewer' : `Show all ${breakdown.rows.length}`}
        </button>
      )}
    </>
  );
}
